import { ApiResponse, PaginatedApiResponse } from "@/types/api.types";
import baseApi from "./RootBaseApi";
import {
  CreateOrganizationPayload,
  OrganizationData,
  OrganizationListQueryParams,
  OrganizationResponse,
  UpdateOrganizationPayload,
  OrganizationTranslationPayload,
  OrganizationTranslations,
} from "@/types/organization.types";
import { UserResponse } from "@/types/user-management.types";
import { RegionResponse } from "@/types/regions.types";

export const OrganizationManagementService = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getOrganizations: builder.query<
      PaginatedApiResponse<OrganizationResponse[]>,
      OrganizationListQueryParams
    >({
      query: (params) => ({
        url: "organizations",
        method: "GET",
        params: {
          page: params.page,
          limit: params.limit,
          status: params.status,
          search: params.search,
        },
      }),
      providesTags: ["OrganizationManagement"],
      keepUnusedDataFor: 0,
    }),
    createOrganization: builder.mutation<
      ApiResponse<OrganizationResponse>,
      CreateOrganizationPayload | FormData
    >({
      query: (organization) => ({
        url: "organizations",
        method: "POST",
        data: organization,
        headers:
          organization instanceof FormData
            ? { "x-lang": "en" }
            : { "x-lang": "en", "Content-Type": "application/json" },
      }),
      invalidatesTags: [{ type: "OrganizationManagement", id: "LIST" }],
    }),
    getOrganizationById: builder.query<OrganizationData, string>({
      query: (organization_id) => ({
        url: `/organizations/${organization_id}`,
        method: "GET",
        headers: { "x-lang": "en" },
      }),
      keepUnusedDataFor: 0,
    }),
    updateOrganization: builder.mutation<
      ApiResponse<OrganizationResponse>,
      { organization_id: string; data: UpdateOrganizationPayload | FormData }
    >({
      query: ({ organization_id, data }) => ({
        url: `/organizations/${organization_id}`,
        method: "PATCH",
        data,
        headers:
          data instanceof FormData
            ? { "x-lang": "en" }
            : { "x-lang": "en", "Content-Type": "application/json" },
      }),
      invalidatesTags: (result, error, { organization_id }) => [
        { type: "OrganizationManagement", id: organization_id },
        { type: "OrganizationManagement", id: "LIST" },
      ],
    }),
    deleteOrganization: builder.mutation<ApiResponse<null>, string>({
      query: (organization_id) => ({
        url: `/organizations/${organization_id}`,
        method: "DELETE",
        headers: { "x-lang": "en" },
      }),
      invalidatesTags: [{ type: "OrganizationManagement", id: "LIST" }],
    }),
    getAdminCandidates: builder.query<
      PaginatedApiResponse<UserResponse>,
      { page?: number; limit?: number; lang?: "en" | "fi" | "de" | "sv" }
    >({
      query: ({ page, limit, lang = "en" }) => ({
        url: "regions/dmo-candidates",
        method: "GET",
        params: { page, limit },
        headers: { "x-lang": lang },
      }),
      keepUnusedDataFor: 0,
    }),
    getRegions: builder.query<
      PaginatedApiResponse<RegionResponse>,
      {
        page?: number;
        limit?: number;
        orphaned?: boolean;
      }
    >({
      query: ({ page, limit, orphaned }) => ({
        url: "regions",
        method: "GET",
        params: { page, limit, orphaned },
        headers: { "x-lang": "en" },
      }),
      providesTags: ["RegionManagement"],
      keepUnusedDataFor: 0,
    }),
    translateOrganizationContent: builder.mutation<
      OrganizationTranslations,
      OrganizationTranslationPayload
    >({
      query: (data) => ({
        url: `organizations/translate`,
        method: "POST",
        data,
      }),
      transformResponse: (response: OrganizationTranslations) => response,
    }),
  }),
});

export const {
  useGetOrganizationsQuery,
  useCreateOrganizationMutation,
  useGetOrganizationByIdQuery,
  useUpdateOrganizationMutation,
  useDeleteOrganizationMutation,
  useGetAdminCandidatesQuery,
  useGetRegionsQuery,
  useTranslateOrganizationContentMutation,
} = OrganizationManagementService;
